import React, { ReactElement, createRef, useEffect, useState } from 'react';
import { Div, Txt } from "Components/GenericView";
import { TextDropdown } from 'Components/TextDropdown';
import { InputField } from 'Components/InputField';
import { UNDEF } from 'Constants';
import { FLEXIBLE_VISIT_TIMING } from 'DataConstants';
import { convertToDestTZ, destOffsetInMillis, minsToAMPM, parseDate, toISOLocal } from 'Util/DateUtils';
import Tooltip from 'Components/Tooltip';
import _ from "lodash";
import './style.css';

export type FlexibleVisitTimingPropType = {
  region?: string;
  disableAnim?: boolean;
  updating?: boolean;
  readOnly?: boolean;
  startDate: any;
  endDate: any;
  startTime: any;
  endTime: any;
  visitTimingFlexibility: string;
  onChange: any;
  handleTimeChange: any;
  hideTooltip: boolean;
}

const FlexibleVisitTiming = (props: FlexibleVisitTimingPropType) => {
  const {
    region,
    disableAnim,
    updating,
    readOnly,
    startDate,
    endDate,
    startTime,
    endTime,
    visitTimingFlexibility,
    onChange,
    handleTimeChange,
    hideTooltip
  } = props;
  const [startTimeList, setStartTimeList] = useState<number[]>([]);
  const [endTimeList, setEndTimeList] = useState<number[]>([]);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const refStartTime = createRef<InputField>();
  const refEndTime = createRef<InputField>();
  const refStartDropdown = createRef<HTMLDivElement>();
  const refEndDropdown = createRef<HTMLDivElement>();

  const generateStartTimeList = () => {
    const startTimes: number[] = [];
    for (let index = 0; index < 96; index++) {
      startTimes.push(index * 15);
    };
    setStartTimeList(startTimes);
  };

  const generateEndTimeList = (selectedStart?: Date) => {
    const endTimes: number[] = [];
    const minDuration = 75; // min duration of 1h 15min

    if (selectedStart) {
      let start = typeof selectedStart === "string" ? parseDate(selectedStart) : selectedStart;

      if (updating) {
        // update case: to add offset from location time
        start = new Date(
          start.getTime() + destOffsetInMillis(region)
        );
      }

      const startTimeMins = start.getHours() * 60 + start.getMinutes();
      for (let index = 0; index < 96; index++) {
        endTimes.push(startTimeMins + minDuration + index * 15);
      }
    } else {
      for (let index = 0; index < 96; index++) {
        endTimes.push(15 + index * 15);
      }
    }
    setEndTimeList(endTimes);
  };

  useEffect(() => {
    generateStartTimeList();
    generateEndTimeList(startTime);
  }, [startTime]);

  const dropdownItemClick = (isStartTime: boolean, minutes: number) => {
    const militaryHours = Math.floor(minutes / 60);
    const h = militaryHours; // % 24;
    const m = minutes - militaryHours * 60;

    let updatedDate = new Date(
      isStartTime ? startDate : endDate!
    );
  
    updatedDate.setHours(h);
    updatedDate.setMinutes(m);

    if (updating) {
      // update case: when needs to minus offset from location time
      updatedDate = new Date(
        updatedDate.getTime() - destOffsetInMillis(region)
      );
    }

    handleTimeChange(
      isStartTime ? updatedDate : UNDEF,
      isStartTime ? UNDEF : updatedDate
    );
  
    blurFields();
    return updatedDate;
  };

  const getSelectedTime = (isStartTime: boolean, minutes: number): boolean => {
    // handling create and update visit with these condition
    let date: any;
  
    if (isStartTime) {
      date = startTime;
    } else {
      date = endTime;
    }

    const selectedMins = !date ? -1 : date.getHours() * 60 + date.getMinutes();
    return selectedMins === minutes % 1440;
  };

  const renderTooltip = (): ReactElement | null => {
    const toolTipText = "CP can only propose time slots that fall within the CO flexible timeframe and don't incur any fee changes.";

    if (showTooltip) {
      return <Tooltip label={toolTipText} />;
    }

    return null;
  };

  const blurFields = () => {
    [refStartTime, refEndTime].forEach((e) =>
      e.current?.blur()
    );
  };

  return <>
    <Div className="flexible-visit-timing">
      <Div className="flexible-visit-timing-dropdown" ml={-8}>
        <TextDropdown
          readOnly={readOnly}
          popupDisabled={readOnly}
          items={FLEXIBLE_VISIT_TIMING}
          placeholder="Select flexibility"
          dontAnimate={disableAnim}
          selectedItem={visitTimingFlexibility}
          onChange={(_: any, selectedItem: string) => 
            onChange(selectedItem)
          }
        />
      </Div>

      {visitTimingFlexibility === 'Yes' && (
        <Div className="h center td-root visit-timing-input" ml={-8} mb={4}>
          <InputField
            ref={refStartTime}
            region={region}
            type="time"
            date={startTime}
            placeholder="Start Time"
            blurOnTabKey
            blurOnEnterKey
            popupHeight="200px"
            updating={updating}
            lineHeight={19}
            dontAnimate={disableAnim}
            readOnly={readOnly}
            popupDisabled={readOnly}
          >
            {!readOnly && (
              <div
                className="dropdown-list dtd-list"
                ref={refStartDropdown}
              >
                {startTimeList.map((time: any, index: any) => (
                  <div
                    key={index}
                    className="dropdown-item dtd-itm"
                    onClick={(_: any) => dropdownItemClick(true, time)}
                    item-selected={getSelectedTime(true, time) ? "" : UNDEF}
                  >
                    {minsToAMPM(time, false)}
                  </div>
                ))}
              </div>
            )}
          </InputField>

          <Txt mh={4}>to</Txt>

          <InputField
            ref={refEndTime}
            type="time"
            date={endTime}
            placeholder="End Time"
            region={region}
            blurOnTabKey
            updating={updating}
            blurOnEnterKey
            lineHeight={19}
            dontAnimate={disableAnim}
            readOnly={readOnly}
            popupDisabled={readOnly}
            popupHeight="200px"
          >
            {!readOnly && (
              <div
                className="dropdown-list dtd-list"
                ref={refEndDropdown}
              >
                {endTimeList.map((time: any, index: any) => (
                  <div
                    key={index}
                    className="dropdown-item dtd-itm"
                    onClick={(_: any) => dropdownItemClick(false, time)}
                    item-selected={getSelectedTime(false, time) ? "" : UNDEF}
                  >
                    {minsToAMPM(time, false)}
                  </div>
                ))}
              </div>
            )}
          </InputField>

          {!hideTooltip && (
            <Div
              pt={2}
              ml={4}
              relative
              className="flexible-visit-timing-tooltip"
              onClick={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}              
            >
              <img alt='info' src='/info.png' />
              {renderTooltip()}
            </Div>
          )}
        </Div>
      )}
    </Div>
  </>
};

export default FlexibleVisitTiming;